import {MNullable} from '../Toolkit/Utils';
import * as Yup from 'yup';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import {SafeDigClient} from './SafeDigsClients';
import {AnySchema} from 'yup';
import Reference from 'yup/lib/Reference';
import {Note} from './NoteTypes';

export class EnvironmentGetReply {
    public environmentName = '';
}

export class SafeDigImport {
    public ID: string = '';
    public Project: string = '';
    public Requestor: string = '';
    public Address1: string = '';
    public Address2: string = '';
    public Address3: string = '';
    public BuildArea: string = '';
    public Postcode: string = '';
    public WorkDescription: string = '';
    public RequiredDate: MNullable<Date> = null;
    public Northing: number = 0;
    public Easting: number = 0;
    public Northing2: number = 0;
    public Easting2: number = 0;
}

export class SafeDig {
    public safeDigsId: number = 0;
    public estimate: string = '';
    public project: number = 0;
    public requestor: string = '';
    public address1: string = '';
    public address2: string = '';
    public address3: string = '';
    public county: string = '';
    public postcode: string = '';
    public workDescription: string = '';
    public receivedDate: MNullable<Date> = null;
    public requiredDate: MNullable<Date> = null;
    public northing: number = 0;
    public easting: number = 0;
    public northing2: number = 0;
    public easting2: number = 0;
    public supervisionDate: MNullable<Date> = null;
    public waterStatus: number = 0;
    public bapaStatus: number = 0;
    public telecomsStatus: number = 0;
    public electricStatus: number = 0;
    public gasStatus: number = 0;
    public lsbudStatus: number = 0;
    public digStatus: number = 0;
    public hotJob: boolean = false;
    public quarantine: boolean = false;
    public parent: number = -1;
    public uploadUser: string = '';
    public uploadDate: MNullable<Date> = null;
    public expiryDate: MNullable<Date> = null;
    public validPermits?: number = -1;
    public completedPermits?: number = -1;
    public noticeCount?: number = -1;
}

export class StatusMessage {
    public type: number = 0;
    public message: string = '';
}

export class Project {
    public id: number = 0;
    public name: string = '';
}

export class CreateSafeDigValidator {
    public static estimateFunc(fileClient: NSwagAdapter<SafeDigClient>): AnySchema | Reference {
        return Yup.string().label('Estimate').max(30).required().test(
            'testDuplicate',
            'Estimate must be unique',
            function testDuplicate(value): Promise<boolean> {
                if(value !== undefined && value!.length <= 30) {
                    return new Promise(async(resolve, reject) => {
                        const currentEstimate = value;
                        const result = await fileClient.callWithReply(
                            CountSafeDigReply, (client) => client.count(-1, currentEstimate));

                        result.throwIfFailed();

                        if(result.reply!.value !== 0) {
                            //Estimate is not unique so cancel the update.
                            resolve(false);
                        } else {
                            resolve(true);
                        }
                    });
                }

                return new Promise(async(resolve, reject) => {
                    resolve(true);
                });
            }
        );
    }

    public static readonly requestor = Yup.string().label('Requestor').max(255).required();
    public static readonly address1 = Yup.string().label('Address1').max(50).required();
    public static readonly address2 = Yup.string().label('Address2').max(50).optional();
    public static readonly address3 = Yup.string().label('Address3').max(50).optional();
    public static readonly county = Yup.string().label('County').max(50).required();
    public static readonly postcode = Yup.string().label('Postcode').max(50).required();
    public static readonly workDescription = Yup.string().label('Work Description').max(500).required();
    public static readonly northing = Yup.number().label('Northing').min(0).max(99999999).required();
    public static readonly easting = Yup.number().label('Easting').min(0).max(99999999).required();
    public static readonly northing2 = Yup.number().label('Northing2').min(0).max(99999999).required();
    public static readonly easting2 = Yup.number().label('Easting2').min(0).max(99999999).required();
    public static readonly requiredDate = Yup.date().label('Required Date').nullable().required();
}

export class UpdateSafeDigValidator {
    public static estimateFunc(fileClient: NSwagAdapter<SafeDigClient>, safeDigId: number): AnySchema | Reference {
        return Yup.string().label('Estimate').max(30).required().test(
            'editDuplicate',
            'Estimate must be unique',
            function testDuplicate(value, ctx): Promise<boolean> {
                if(value !== undefined && value!.length <= 30) {
                    return new Promise(async(resolve, reject) => {
                        const currentEstimate = value;
                        const result = await fileClient.callWithReply(
                            CountSafeDigReply, (client) => client.count(ctx.parent.safeDigsId, currentEstimate));
                        result.throwIfFailed();

                        if(result.reply!.value !== 0) {
                            //Estimate is unique.
                            resolve(false);
                        } else {
                            resolve(true);
                        }
                    });
                }

                return new Promise(async(resolve, reject) => {
                    resolve(true);
                });
            }
        );
    }

    public static readonly address1 = Yup.string().label('Address1').max(50).required();
    public static readonly address2 = Yup.string().label('Address2').max(50).optional();
    public static readonly address3 = Yup.string().label('Address3').max(50).optional();
    public static readonly county = Yup.string().label('County').max(50).required();
    public static readonly postcode = Yup.string().label('Postcode').max(50).required();
    public static readonly workDescription = Yup.string().label('Work Description').max(500).required();
    public static readonly northing = Yup.number().label('Northing').min(0).max(99999999).required();
    public static readonly easting = Yup.number().label('Easting').min(0).max(99999999).required();
    public static readonly northing2 = Yup.number().label('Northing2').min(0).max(99999999).required();
    public static readonly easting2 = Yup.number().label('Easting2').min(0).max(99999999).required();
    public static readonly requiredDate = Yup.date().label('Required Date').nullable().optional();
}

export class ProjectReply {
    public data: Project[] = [];
}

export class SafeDigReply {
    public data: MNullable<SafeDig> = null;
}

export class SafeDigListReply {
    public data: SafeDig[] = [];
}

export class StatusMessageReply {
    public data: StatusMessage[] = [];
}

export class CountSafeDigReply {
    public value: number = 0;
    public statusCode: number = 0;
    public message: string = '';
}

export class CreateSafeDigReply {
    public data: MNullable<SafeDig> = null;
    public statusCode: number = 0;
    public message: string = '';
}

export class UpdateSafeDigReply {
    public value: number = 0;
    public statusCode: number = 0;
    public message: string = '';
}

export class CancelSafeDigReply {
    public value: number = 0;
    public statusCode: number = 0;
    public message: string = '';
}

export class SafeDigNoteReply {
    public data: Note[] = [];
}

